export enum WorkOrderStatus {
  NEW = "NEW", // created from approved quotation
  IN_PROGRESS = "IN_PROGRESS", // work started on site
  ON_HOLD = "ON_HOLD", // paused due to issue
  READY_FOR_BILLING = "READY_FOR_BILLING", // approved, accounts to finalise and generate invoice
  COMPLETED = "COMPLETED", // billing submitted; work assignments closed
  CANCELLED = "CANCELLED", // job cancelled
}

export enum WorkOrderSourceType {
  QUOTATION = "QUOTATION",
  // AMC = "AMC",
  // /** Work order created from a completed AMC visit (chargeable follow-up job). */
  // AMC_VISIT = "AMC_VISIT",
}

export const WorkOrderSourceTypeLabelMap: Record<WorkOrderSourceType, string> =
  {
    [WorkOrderSourceType.QUOTATION]: "Quotation",
    // [WorkOrderSourceType.AMC]: "Annual Maintenance Contract",
    // [WorkOrderSourceType.AMC_VISIT]: "AMC visit (follow-up)",
  };

type WorkOrderStatusBadge = {
  label: string;
  color: string;
  hint: string;
};

const workOrderStatusBadgeByEnum: Record<
  WorkOrderStatus,
  WorkOrderStatusBadge
> = {
  [WorkOrderStatus.NEW]: {
    label: "New",
    color: "primary",
    hint: "Created from an approved quotation. Edit scope, line items, crew, and schedule before starting work.",
  },
  [WorkOrderStatus.IN_PROGRESS]: {
    label: "In Progress",
    color: "warning",
    hint: "Work is actively underway. Update progress, materials, and time until completion.",
  },
  [WorkOrderStatus.ON_HOLD]: {
    label: "On hold",
    color: "secondary",
    hint: "Paused due to an issue. Scheduling and execution may resume when cleared.",
  },
  [WorkOrderStatus.READY_FOR_BILLING]: {
    label: "Ready for Billing",
    color: "primary",
    hint: "Completion approved. Accounts can now finalise the billed amount and generate the invoice.",
  },
  [WorkOrderStatus.COMPLETED]: {
    label: "Completed",
    color: "success",
    hint: "Billing submitted and invoice generated. Work assignments are closed.",
  },
  [WorkOrderStatus.CANCELLED]: {
    label: "Cancelled",
    color: "danger",
    hint: "Stopped before completion. No further work should be scheduled on this order.",
  },
};

/** Shown on work order status badges (list, overview) as label, color, and hover tooltip. */
export const workOrderStatusMap: Record<string, WorkOrderStatusBadge> = {
  ...workOrderStatusBadgeByEnum,
  /** @deprecated API may still return until migrated; same meaning as {@link WorkOrderStatus.NEW}. */
  DRAFT: workOrderStatusBadgeByEnum[WorkOrderStatus.NEW],
  /** @deprecated API may still return until migrated; treat as {@link WorkOrderStatus.NEW}. */
  ASSIGNED: workOrderStatusBadgeByEnum[WorkOrderStatus.NEW],
};

/** Only the earliest editable work orders may be deleted from the list UI. */
export function workOrderAllowsDelete(
  status: string | null | undefined,
): boolean {
  return status === WorkOrderStatus.NEW;
}

/** Terminal-ish states: no further record edits from the main edit form. */
const WO_STATUSES_BLOCK_RECORD_UPDATE: ReadonlySet<string> = new Set([
  WorkOrderStatus.READY_FOR_BILLING,
  WorkOrderStatus.COMPLETED,
  WorkOrderStatus.CANCELLED,
]);

/**
 * When false, the work order record should not be updated from the UI (header,
 * financials, schedule, expenses, subcontracts on the edit form). Allowed for
 * {@link WorkOrderStatus.NEW}, {@link WorkOrderStatus.IN_PROGRESS},
 * {@link WorkOrderStatus.ON_HOLD}, and legacy `DRAFT` / `ASSIGNED`.
 *
 * Line items (BOQ) are governed separately — see {@link workOrderAllowsLineItemUpdate} —
 * so a corrected invoice can be regenerated after completion.
 */
export function workOrderAllowsRecordUpdate(
  status: string | null | undefined,
): boolean {
  const s = String(status ?? "").trim();
  if (s === "") return true;
  return !WO_STATUSES_BLOCK_RECORD_UPDATE.has(s);
}

/**
 * Line items (BOQ) stay editable through every status except
 * {@link WorkOrderStatus.CANCELLED} — including after completion, so a
 * corrected invoice can be generated via regenerate-invoice.
 */
export function workOrderAllowsLineItemUpdate(
  status: string | null | undefined,
): boolean {
  const s = String(status ?? "").trim();
  if (s === "") return true;
  return s !== WorkOrderStatus.CANCELLED;
}

/** Work is underway or paused: allow controlled edits (not the same as planning-only NEW). */

/** Completed / closed / cancelled: no new MRs, no owner change, etc. */
const WO_STATUSES_BLOCK_POST_JOB_ACTIONS: ReadonlySet<string> = new Set([
  WorkOrderStatus.READY_FOR_BILLING,
  WorkOrderStatus.COMPLETED,
  WorkOrderStatus.CANCELLED,
]);

/** Blocks MRs once the work order enters the approval pipeline or beyond — mirrors backend. */
export function workOrderAllowsMaterialRequest(
  status: string | null | undefined,
): boolean {
  const s = String(status ?? "").trim();
  if (s === "") return false;
  return !WO_STATUSES_BLOCK_POST_JOB_ACTIONS.has(s);
}

/** False once the job is finished or cancelled — primary owner is fixed in the UI. */
export function workOrderAllowsOwnerReassign(
  status: string | null | undefined,
): boolean {
  const s = String(status ?? "").trim();
  if (s === "") return true;
  return !WO_STATUSES_BLOCK_POST_JOB_ACTIONS.has(s);
}
